import React, { useState, useEffect, useRef } from "react";
import { Button, Container, Spinner } from "reactstrap";
import { formatDate, showToastMessage } from "common/service/CommonService";
import styles from "../../../../modules/Library/AdverseEvent/css/adverseEvent.module.scss";
import ip from "../../IPinventory/css/ipInventory.module.scss";
import CommonTableIndexWise, { Column } from "Components/Common/CommonTableIndexWise";
import { GetUserData } from "common/service/CommonService";
import { usePharmacyLedgerService } from "modules/Pharmacy/DailyReconsilation/api/usePharmacyLedger";
import { queryClient } from "api/queryClient";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "../../../../common/constants/PermissionConstants";
import AddActualCountModal from "../components/AddActualCountModal";
import PinVerificationModal from "../components/PinVerificationModal";
import CommonSearch from "Components/Common/CommonSearch";
import StatCard from "../../../../Components/Common/StatCard";
import { useGlobalStudy } from "../../../../context/StudyContext";
import NoStudySelectedPlaceholder from "../../../../Components/Common/NoStudySelectedPlaceholder";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import FilePreviewModal from "modules/Study/Attachments/components/FilePreviewModal";
import CommonButton from "Components/Common/CommonButton";

/* ------------------------------------------------------------------ */
/* Interfaces                                                            */
/* ------------------------------------------------------------------ */

export interface StockRecord {
  stockid: number;
  productname: string;
  ipcategory: string;
  batchlotno: string;
  received: number;
  dispensed: number;
  returned: number;
  systemexp: number;
  actualcount: number | null;
  variance: number | null;
  status: string;
  total_count: number;
  studyid: number;
  studycode: string;
  accountabilitydate?: string;
  accountabilityid: number;
  isverified?: boolean;
}

interface StatCardProps {
  title: string;
  value: number | string;
  subtext: string;
  icon: React.ReactNode;
  subtextIcon?: string;
  bgColor?: string;
  iconColor?: string;
  trendType?: "info" | "negative" | "success";
  valueColor?: string;
}

const StockReconciliationList: React.FC = () => {
  document.title = "Daily Stock Reconciliation | ZENTIXS ORBIT";

  const userData = GetUserData();

  const [searchText, setSearchText] = useState<string>("");
  const { selectedStudy: globalStudy } = useGlobalStudy();
  const [currentPageNumber, setcurrentPageNumber] = useState<number>(1);
  type TabFilter = "All" | "Test Product" | "Reference Product";
  const [filter, setFilter] = useState<TabFilter>("All");

  // Add Actual Count Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<StockRecord | null>(null);
  const [verifyModal, setVerifyModal] = useState<boolean>(false);

  //Audit
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();

  // Reset to page 1 whenever the global study filter changes
  const prevStudyRef = useRef(globalStudy?.studyid);
  useEffect(() => {
    if (prevStudyRef.current !== globalStudy?.studyid) {
      prevStudyRef.current = globalStudy?.studyid;
      setcurrentPageNumber(1);
    }
  }, [globalStudy?.studyid]);
  /* ── Service ── */
  const { ActualCountMutation, UpdateActualCountMutation, SelectAllDailyAccountabilityQuery, verifyPinMutation } = usePharmacyLedgerService({
    searchText,
    pageNumber: currentPageNumber,
    pageSize: 5,
    studyid: globalStudy?.studyid,
    ipcategory: filter,
    enableSelectall: true,
  });

  const {
    data: queryResponse,
    isLoading,
    isFetching,
    isError,
  } = SelectAllDailyAccountabilityQuery as {
    data?: { data?: any[] };
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
  };

  const [localData, setLocalData] = useState<StockRecord[]>([]);

  React.useEffect(() => {
    if (queryResponse?.data) {
      const mapped = (queryResponse.data ?? []).map((row: any, index: number) => ({
        accountabilityid: row.accountabilityid,
        accountabilitydate: row.accountabilitydate,
        openingstock: row.openingstock ?? 0,
        stockid: row.stockid || row.ledgerid || row.accountabilityid || (index + 1 + (currentPageNumber - 1) * 5),
        studyid: row.studyid ?? "",
        studycode: row.studycode ?? "",
        productname: row.productname ?? "",
        ipcategory: row.ipcategory ?? "",
        batchlotno: row.batchlotno ?? "",
        received: row.received ?? row.receivedqty ?? 0,
        dispensed: row.dispensed ?? row.dispensedqty ?? 0,
        returned: row.returned ?? row.returnedqty ?? 0,
        disposed: row.disposed ?? row.disposedqty ?? 0,
        transfered: row.transfered ?? row.transferedqty ?? 0,
        closingstock: row.closingstock ?? row.closingstock ?? 0,
        systemexp: row.closingstock ?? row.systemexp ?? 0,
        actualcount: row.actualcount ?? null,
        variance: row.dailyvariance ?? null,
        status: row.variancestauts ?? "-",
        total_count: row.total_count ?? 0,
        reserved: row.retention ?? row.reserved ?? 0,
        isverified: row.isverified ?? false,
        verifiedby: row.verifiedby ?? "-",
        verifieddate: formatDate(row.verifieddate) ?? "",
      }));
      setLocalData(mapped);
    }
  }, [queryResponse, currentPageNumber]);

  /* ── Handlers ── */
  const onSearch = (value: string | null) => {
    setSearchText(value ?? "");
    setcurrentPageNumber(1);
  };

  const handleActualCountClick = (row: StockRecord) => {
    setSelectedRecord(row);
    setIsAddModalOpen(true);
  };

  const handleSaveActualCount = (data: { actualcount: number; reason: string; remarks: string; updatereason?: string }) => {
    console.log("Saving actual count for:", selectedRecord);
    const isEdit = selectedRecord?.actualcount !== null;

    const payload = {
      studyid: selectedRecord?.studyid ?? 0,
      ipcategory: selectedRecord?.ipcategory ?? "",
      productname: selectedRecord?.productname ?? "",
      batchlotno: selectedRecord?.batchlotno ?? "",
      accountabilityid: selectedRecord?.accountabilityid ?? 0,
      systemexpected: selectedRecord?.systemexp ?? 0,
      actualcount: data.actualcount ?? 0,
      reason: data.reason ?? "",
      remarks: data.remarks ?? "",
      createdby: userData?.userid ?? 0,
      updatedby: userData?.userid ?? 0,
      updatereason: data.updatereason ?? "",
    };

    const mutation = isEdit ? UpdateActualCountMutation : ActualCountMutation;

    mutation.mutate(payload, {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries({ queryKey: ["daily-accountability-selectAll"] });
        // queryClient.invalidateQueries({ queryKey: ["pharmacy-ledger-selectAll"] });
        showToastMessage("success", res?.message || res?.data?.message || (isEdit ? "Actual count updated successfully" : "Actual count saved successfully"));
        setIsAddModalOpen(false);
      },
      onError: (err: any) => {
        showToastMessage("error", err || (isEdit ? "Failed to update actual count" : "Failed to save actual count"));
      }
    });
  };

  const handleVerifyClick = (row: StockRecord) => {
    setSelectedRecord(row);
    setVerifyModal(true);
  };

  const handlePinVerify = (password: string) => {
    if (!selectedRecord) return;
    const payload = {
      verifypin: password,
      accountabilityid: selectedRecord.accountabilityid,
      studyid: selectedRecord.studyid,
      verifiedby: userData?.userid ?? 0,
    };
    verifyPinMutation.mutate(payload, {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries({ queryKey: ["daily-accountability-selectAll"] });
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
        // queryClient.invalidateQueries({ queryKey: ["pharmacy-ledger-selectAll"] });
        showToastMessage("success", res?.message || res?.data?.message || "Verification successful");
        setVerifyModal(false);
      },
      onError: (err: any) => {
        showToastMessage("error", err || "Verification failed");
      }
    });
  };

  const onAuditClick = () => {
    const payload = {
      modulename: "Stock Reconciliation",
      studyid: globalStudy?.studyid
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Stock_Reconciliation_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };


  /* ------------------------------------------------------------------ */
  /* Columns                                                              */
  /* ------------------------------------------------------------------ */

  const columns: Column[] = [
    {
      key: "accountabilitydate",
      label: "ACCOUNTABILITY DATE",
      sticky: true,
      width: "160px",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{formatDate(row.accountabilitydate)}</p>
          </>
        )
      }
    },
    {
      key: "studycode",
      label: "STUDY CODE",
      sticky: true,
      width: "140px",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.studycode || "-"}</p>
            <p className={`${ip.cellSeconadary} mb-0`}>{row.ipcategory || "-"}</p>
          </>
        );
      },
    },
    {
      key: "productname",
      label: "PRODUCT NAME",
      sticky: true,
      divider: true,
      tooltip: true,
      maxLength: 15,
      // width: "180px",
      // render: (_: any, row?: any) => {
      //   if (!row) return null;
      //   return (
      //     <>
      //       <p className={ip.cellPrimary}>{row.productname || "-"}</p>
      //       <p className={`${ip.cellSeconadary} mb-0`}>{row.batchlotno || "-"}</p>
      //     </>
      //   );
      // },
      render: (_: any, row?: any) => {
        if (!row) return null;

        const productName = row.productname || "-";
        const max = 12; // Set your desired character limit here
        const isLong = productName.length > max;
        const displayedName = isLong ? productName.slice(0, max) + "..." : productName;
        return (
          <>
            <p className={ip.cellPrimary} title={isLong ? productName : ""}>
              {displayedName}
            </p>
            <p className={`${ip.cellSeconadary} mb-0`}>{row.batchlotno || "-"}</p>
          </>
        );
      },
    },
    {
      key: "opningstock",
      label: "OPENING STOCK",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return <p className={ip.cellPrimary}>{row.openingstock ?? "-"}</p>;
      },
    },
    {
      key: "received",
      label: "RECEIVED",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return <p className={ip.cellPrimary}>{row.received ?? "-"}</p>;
      },
    },
    {
      key: "dispensed",
      label: "DISPENSED",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return <p className={ip.cellPrimary}>{row.dispensed ?? "-"}</p>;
      },
    },
    {
      key: "returned",
      label: "RETURNED",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return <p className={ip.cellPrimary}>{row.returned ?? "-"}</p>;
      },
    },
    {
      key: "reserved",
      label: "RESERVED",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return <p className={ip.cellPrimary}>{row.reserved ?? "-"}</p>;
      },
    },
    {
      key: "disposed",
      label: "DISPOSED",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return <p className={ip.cellPrimary}>{row.disposed ?? "-"}</p>;
      },
    },
    {
      key: "transfered",
      label: "TRANSFERED",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return <p className={ip.cellPrimary}>{row.transfered ?? "-"}</p>;
      },
    },
    {
      key: "closingstock",
      label: "CLOSING STOCK",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return <p className={ip.cellPrimary}>{row.closingstock ?? "-"}</p>;
      },
    },
    // {
    //   key: "systemexp",
    //   label: "SYSTEM EXP.",
    //   render: (_: any, row?: any) => {
    //     if (!row) return null;
    //     return <strong className={ip.cellPrimary}>{row.systemexp ?? "-"}</strong>;
    //   },
    // },
    {
      key: "actualcount",
      label: "ACTUAL COUNT",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return row.actualcount != null
          ? <strong className={ip.cellPrimary}>{row.actualcount}</strong>
          : <span className={ip.cellSecondary}>--</span>;
      },
    },
    {
      key: "variance",
      label: "VARIANCE",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return <strong className={ip.cellPrimary}>{row.variance ?? "-"}</strong>;
      },
    },
    {
      key: "status",
      label: "STATUS",
      render: (_: any, row?: any) => {
        return (
          <strong className={row.variance == 0 ? `${ip.statCardIconSuccess}` : (row.variance < 0 ? `${ip.statCardIconWarning}` : (row.variance > 0 ? `${ip.statCardIconExcess}` : ``))}>
            {row.status || "-"}
          </strong>
        );
      },
    },
    {
      key: "verifiedby",
      label: "VERIFIED BY",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.verifiedby || "-"}</p>
            <p className={`${ip.cellSeconadary} mb-0`}>{row.verifieddate || "-"}</p>
          </>
        );
      },
    },

  ];

  const getRowActions = (row: any): any => {
    const actualcount = row?.actualcount;
    const isVerified = row?.isverified;

    const actions: any = {};
    if (actualcount === null) {
      actions.onActualCount = (row: any) => handleActualCountClick(row);
    } else {
      if (!isVerified) {
        actions.onEditActualCount = (row: any) => handleActualCountClick(row);
        actions.onVerify = (row: any) => handleVerifyClick(row);
      }
    }
    return actions;
  };

  /* ------------------------------------------------------------------ */
  /* Render                                                               */
  /* ------------------------------------------------------------------ */

  return (
    <div className={styles.dashboardContainer}>
      <Container fluid>

        {!globalStudy?.studyid ? (
          <div >
            <NoStudySelectedPlaceholder moduleName="stock reconciliation records" />
          </div>
        ) : (
          <>
            {/* ── Page Header ── */}
            <div className="d-flex justify-content-between align-items-center mt-4 mb-3 px-2">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <h5 className={`mb-0 ${styles.pageHeaderTitle}`}>Daily Stock Reconciliation</h5>
                </div>
                <p className={styles.pageHeaderSubtitle}>
                  Verify physical inventory against system records for accountability.
                </p>
              </div>
              <div className="d-flex gap-3 align-items-center">
                <CommonButton
                  module={PERMISSION_MODULES.STOCK_RECONCILIATION}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                  className="fw-600 "
                  size="sm"
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
              </div>
            </div>
            {/* ── Stat Cards ── */}
            <div className="row g-3 mb-3 px-1">
              <div className="col-md-3">
                <StatCard
                  title="TOTAL RECEIVED"
                  value={Number(localData.reduce((s: number, r: StockRecord) => s + (r.received || 0), 0))}
                  subtext="Units across all batches"
                  icon={<i className="ri-inbox-archive-line" />}
                  bgColor="#ecfdf5"
                  iconColor="#10b981"
                  trendType="success"
                  subtextIcon="ri-arrow-left-down-line"
                />
              </div>
              <div className="col-md-3">
                <StatCard
                  title="TOTAL DISPENSED"
                  value={Number(localData.reduce((s: number, r: StockRecord) => s + (r.dispensed || 0), 0))}
                  subtext="To randomized subjects"
                  icon={<i className="ri-share-forward-line" />}
                  bgColor="#eff6ff"
                  iconColor="#3b82f6"
                  trendType="info"
                  subtextIcon="ri-arrow-right-up-line"
                />
              </div>
              <div className="col-md-3">
                <StatCard
                  title="TOTAL RETURNED"
                  value={Number(localData.reduce((s: number, r: StockRecord) => s + (r.returned || 0), 0))}
                  subtext="From subjects or sites"
                  icon={<i className="ri-arrow-go-back-line" />}
                  bgColor="#fef3c7"
                  iconColor="#d97706"
                  trendType="negative"
                  subtextIcon="ri-error-warning-line"
                />
              </div>
              <div className="col-md-3">
                <StatCard
                  title="CURRENT SYSTEM BALANCE"
                  value={Number(localData.reduce((s: number, r: StockRecord) => s + (r.systemexp || 0), 0))}
                  subtext="Expected physical count"
                  icon={<i className="ri-scales-3-line" />}
                  bgColor="#eff6ff"
                  iconColor="#1d4ed8"
                  trendType="info"
                  subtextIcon="ri-information-line"
                />
              </div>
            </div>

            {/* ── Table Card ── */}
            <div className={styles.listPanel}>
              {/* Panel Header */}
              <div className={styles.panelHeader}>
                <div className="d-flex justify-content-between align-items-center w-100">
                  <div className="d-flex gap-2">
                    <button
                      className={`btn btn-sm rounded-pill px-3 ${filter === "All" ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => { setFilter("All"); setcurrentPageNumber(1); }}
                    >
                      All
                    </button>
                    <button
                      className={`btn btn-sm rounded-pill px-3 ${filter === "Test Product" ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => { setFilter("Test Product"); setcurrentPageNumber(1); }}
                    >
                      Test Product
                    </button>
                    <button
                      className={`btn btn-sm rounded-pill px-3 ${filter === "Reference Product" ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => { setFilter("Reference Product"); setcurrentPageNumber(1); }}
                    >
                      Reference Product
                    </button>
                  </div>

                  <CommonSearch onSearch={onSearch} searchText={searchText} />
                </div>
              </div>

              {/* Table */}
              <div className="px-3">
                <CommonTableIndexWise
                  moduleName={PERMISSION_MODULES.STOCK_RECONCILIATION}
                  columns={columns}
                  data={localData}
                  loading={isLoading || isFetching}
                  pageSize={5}
                  currentPage={currentPageNumber}
                  totalRecords={localData[0]?.total_count || 0}
                  onPageChange={setcurrentPageNumber}
                  actions={getRowActions}
                  visitScroll
                />
              </div>
            </div>
          </>
        )}
      </Container>


      {/* ── Add Single Actual Count Modal ── */}
      <AddActualCountModal
        isOpen={isAddModalOpen}
        toggle={() => setIsAddModalOpen(false)}
        record={selectedRecord}
        onSave={handleSaveActualCount}
        isLoading={UpdateActualCountMutation.isPending || ActualCountMutation.isPending}
      />

      {/* File Preview Modal */}
      {isPreviewOpen && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size={"xl"}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}

      {/* ── Pin Verification Modal ── */}
      <PinVerificationModal
        isOpen={verifyModal}
        toggle={() => setVerifyModal(false)}
        onVerify={handlePinVerify}
        isLoading={verifyPinMutation.isPending}
        title="Verify Stock Reconciliation Record"
        description={
          <>
            Please enter your password to verify the stock reconciliation record for product{" "}
            <strong>{selectedRecord?.productname}</strong>.
          </>
        }
        isInvalid={verifyPinMutation.isError}
      />
    </div>
  );
};

export default StockReconciliationList;